"use client";

import { useSyncExternalStore } from "react";
import { useLingui } from "@lingui/react/macro";
import {
  EnglishFallbackNotice as SharedEnglishFallbackNotice,
  LocaleSwitcher as SharedLocaleSwitcher,
  LocaleSwitcherLinks as SharedLocaleSwitcherLinks,
} from "@marketing-sites/ui/components";
import {
  readLocalePreference,
  writeLocalePreference,
} from "@marketing-sites/l10n/preference/cookie";
import {
  CONSENT_CHANGED_EVENT,
  hasFunctionalConsent,
} from "@marketing-sites/marketo/consent";
import { cn } from "@marketing-sites/utils/cn";
import { stripLocalePrefix } from "@marketing-sites/l10n/route";
import { usePathname } from "next/navigation";

import { containerVariants } from "@/components/atoms/container";
import { localeConfig, UNLISTED_LOCALE_PREFIXES } from "@/lib/l10n/config";
import { localizeHref } from "@/lib/l10n/href";
import { launchedRoutesByLocale } from "@/lib/l10n/routes";

// Off the URL, not LocaleProvider context: the interim locale dirs mount no provider.
function useLocaleFromPathname(): { prefix: string; logicalPath: string } {
  const pathname = usePathname();
  const { prefix, logicalPath } = stripLocalePrefix(pathname, localeConfig);
  if (prefix) return { prefix, logicalPath };

  const [, first = "", ...rest] = pathname.split("/");
  if (UNLISTED_LOCALE_PREFIXES.includes(first)) {
    return { prefix: first, logicalPath: `/${rest.join("/")}` };
  }
  return { prefix: localeConfig.DEFAULT_PREFIX, logicalPath };
}

// A locale with nothing launched would link to its home, which redirects back to English.
const LAUNCHED_LOCALES = launchedRoutesByLocale();
const SWITCHABLE_PREFIXES = [
  localeConfig.DEFAULT_PREFIX,
  ...localeConfig.KNOWN_PREFIXES.filter(
    (prefix) =>
      prefix !== localeConfig.PSEUDO_PREFIX &&
      LAUNCHED_LOCALES.has(localeConfig.PREFIX_TO_LOCALE[prefix]),
  ),
];

// Falls back to the locale's home when localizeHref leaves an unlaunched route unchanged.
export function resolveLocaleHref(logicalPath: string, prefix: string): string {
  if (prefix === localeConfig.DEFAULT_PREFIX) return logicalPath;
  const localized = localizeHref(logicalPath, prefix);
  return localized === logicalPath ? `/${prefix}` : localized;
}

export function useLocaleSwitcherHeading(): string {
  const { t } = useLingui();
  return t`Language`;
}

function useLocaleSwitcherLinkProps() {
  const { prefix: currentPrefix, logicalPath } = useLocaleFromPathname();

  return {
    prefixes: SWITCHABLE_PREFIXES,
    currentPrefix,
    resolveHref: (prefix: string) => resolveLocaleHref(logicalPath, prefix),
    resolveLang: (prefix: string) =>
      localeConfig.resolveLocaleFromPrefix(prefix),
    onSelect: (prefix: string) =>
      writeLocalePreference(localeConfig, prefix, {
        hasConsent: hasFunctionalConsent,
      }),
  };
}

function localeLinkClassName(isThemeDark?: boolean) {
  return cn(
    "text-base leading-[125%] opacity-90 hover:opacity-100 focus:opacity-100",
    isThemeDark ? "text-white" : "text-black",
  );
}

export function LocaleSwitcher({
  className,
  isThemeDark,
}: {
  className?: string;
  isThemeDark?: boolean;
}) {
  const linkProps = useLocaleSwitcherLinkProps();
  const heading = useLocaleSwitcherHeading();

  return (
    <SharedLocaleSwitcher
      {...linkProps}
      heading={heading}
      className={cn("flex flex-col gap-3 pb-8", className)}
      headingClassName="text-xs font-medium uppercase leading-[130%] tracking-[1.2px]"
      listClassName="flex flex-col gap-1"
      linkClassName={localeLinkClassName(isThemeDark)}
    />
  );
}

export function LocaleSwitcherLinks({
  className,
  isThemeDark,
}: {
  className?: string;
  isThemeDark?: boolean;
}) {
  const linkProps = useLocaleSwitcherLinkProps();

  return (
    <SharedLocaleSwitcherLinks
      {...linkProps}
      className={cn("mb-5 flex flex-col gap-1", className)}
      linkClassName={cn("block py-0.5", localeLinkClassName(isThemeDark))}
    />
  );
}

// The cookie only exists client-side, so the server snapshot is null and the real
// value arrives at hydration. useSyncExternalStore rather than an effect, which
// would setState on every mount.
//
// Consent withdrawal is the one thing that invalidates the snapshot mid-session,
// so it is also what the store subscribes to. The snapshot is a string or null,
// so repeated reads compare equal and never loop.
function subscribeToConsentChange(onStoreChange: () => void) {
  window.addEventListener(CONSENT_CHANGED_EVENT, onStoreChange);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onStoreChange);
}

function usePreferredPrefix(): string | null {
  return useSyncExternalStore(
    subscribeToConsentChange,
    () =>
      readLocalePreference(localeConfig, { hasConsent: hasFunctionalConsent }),
    () => null,
  );
}

export function EnglishFallbackNotice({ className }: { className?: string }) {
  const { prefix: currentPrefix, logicalPath } = useLocaleFromPathname();
  const preferredPrefix = usePreferredPrefix();

  return (
    <SharedEnglishFallbackNotice
      preferredPrefix={preferredPrefix}
      isPrefixedPath={currentPrefix !== localeConfig.DEFAULT_PREFIX}
      hasPreferredTranslation={
        !!preferredPrefix &&
        localizeHref(logicalPath, preferredPrefix) !== logicalPath
      }
      resolveHref={(prefix) => resolveLocaleHref(logicalPath, prefix)}
      className={cn(
        containerVariants({ size: "lg" }),
        "mb-10 text-sm md:mb-12 md:px-6 md:text-base",
        className,
      )}
      linkClassName="underline underline-offset-2 hover:opacity-70"
      dismissClassName="px-2 text-lg leading-none opacity-60 hover:opacity-100"
    />
  );
}
